import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { StyleSheet, View } from '../compiler'

import { colors, typoStyles } from '../constants'
import { useTranslationContext } from '../context/general-translations'
import DiscussionQuestions from './DiscussionQuestions'
import BeamGraph from './graphs/BeamGraph'
import MarkdownText from './MarkdownText'

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 24
  },
  headerContainerStyle: {
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 16,
    borderWidth: 1,
    borderColor: colors.lightGrey,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8
  },
  contentContainerStyle: {
    paddingHorizontal: 24,
    paddingTop: 28,
    paddingBottom: 16,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: colors.lightGrey
  },
  footerContainerStyle: {
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    backgroundColor: colors.lightPaleGrey,
    borderWidth: 1,
    borderColor: colors.lightGrey,
    padding: 24
  }
})

const DetailedDimensionCard = ({
  style = {},
  dimension,
  challenges,
  beamGraphData,
  textModules,
  language,
  isLoading
}) => {
  const { tGeneral } = useTranslationContext()

  const { property, intro, scaleEndpoints, measured_by } = dimension
  const { left, right, description } = scaleEndpoints

  return (
    <View style={[styles.container, style]}>
      <View style={styles.headerContainerStyle}>
        <View>
          <View style={{ flexDirection: 'row', alignItems: 'flex-end' }}>
            <MarkdownText style={typoStyles.H4Black}>
              {isLoading ? <Skeleton width={150} /> : property[language]}
            </MarkdownText>
            {!isLoading && beamGraphData.dataset_not_complete && (
              <MarkdownText
                style={[
                  typoStyles.SmallBlack,
                  { marginLeft: 4, paddingBottom: 1 }
                ]}
              >
                {tGeneral('dataset_not_complete')}
              </MarkdownText>
            )}
          </View>

          <MarkdownText
            style={[
              typoStyles.T1Black,
              { display: 'block', color: colors.rockGrey, marginTop: 4 }
            ]}
          >
            {isLoading ? <Skeleton count={2} /> : intro[language]}
          </MarkdownText>
          <MarkdownText
            style={[
              typoStyles.T2Black,
              { display: 'block', color: colors.rockGrey, marginTop: 8 }
            ]}
          >
            {isLoading ? (
              <Skeleton width={200} />
            ) : (
              <>
                {tGeneral('method_label')}
                <MarkdownText style={typoStyles.ButtonBlue}>
                  {measured_by
                    .map((exam_id) => challenges.data[exam_id].title[language])
                    .join(', ')}
                </MarkdownText>
              </>
            )}
          </MarkdownText>
        </View>
      </View>
      <View style={styles.contentContainerStyle}>
        {isLoading ? (
          <Skeleton height={40} />
        ) : (
          <BeamGraph {...beamGraphData} />
        )}
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginTop: 8
          }}
        >
          <MarkdownText style={typoStyles.SmallBlack}>
            {isLoading ? <Skeleton width={80} /> : left.title[language]}
          </MarkdownText>
          <MarkdownText style={typoStyles.SmallBlack}>
            {isLoading ? <Skeleton width={80} /> : right.title[language]}
          </MarkdownText>
        </View>
        {left.description && right.description && (
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginTop: 4
            }}
          >
            <MarkdownText
              style={[
                typoStyles.SmallBlack,
                { color: colors.rockGrey, maxWidth: '32%', opacity: 0.8 }
              ]}
            >
              {left.description[language]}
            </MarkdownText>
            <MarkdownText
              style={[
                typoStyles.SmallBlack,
                {
                  color: colors.rockGrey,
                  maxWidth: '32%',
                  textAlign: 'right',
                  opacity: 0.8
                }
              ]}
            >
              {right.description[language]}
            </MarkdownText>
          </View>
        )}
        {description && (
          <View style={{ marginTop: 4 }}>
            <MarkdownText
              style={[
                typoStyles.SmallBlack,
                { display: 'block', color: colors.rockGrey, opacity: 0.8 }
              ]}
            >
              {description[language]}
            </MarkdownText>
          </View>
        )}
      </View>
      <View style={styles.footerContainerStyle}>
        <MarkdownText style={typoStyles.T1Black}>
          {isLoading ? (
            <Skeleton count={3} />
          ) : (
            textModules.text_long || textModules.text
          )}
        </MarkdownText>
        {!isLoading &&
          textModules.discussion_questions &&
          Array.isArray(textModules.discussion_questions) && (
            <DiscussionQuestions
              style={{ marginTop: 24 }}
              questions={textModules.discussion_questions}
            />
          )}
      </View>
    </View>
  )
}

export default DetailedDimensionCard
